import * as React from 'react';

import {
  type CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  type Time,
  today,
  toZoned,
} from '@internationalized/date';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { variableUpdateAtom } from '../../../_graphql/mutationVariableCrud';
import {
  $effectiveDate,
  $effectiveTime,
  $pendingChanges,
  $pendingCount,
  $remoteVariables,
} from '../../../_state/variablesList.state';
import { useTranslations } from '../../../i18n/useTranslations';
import type { PendingVariableChange } from '../../../types/pricing';

export const useEditableVariableTable = () => {
  const t = useTranslations();

  const [pendingChanges, setPendingChanges] = useAtom($pendingChanges);
  const [effectiveDate, setEffectiveDate] = useAtom($effectiveDate);
  const [effectiveTime, setEffectiveTime] = useAtom($effectiveTime);
  const [userChangedDateTime, setUserChangedDateTime] = React.useState(false);
  const pendingCount = useAtomValue($pendingCount);
  const { refetch } = useAtomValue($remoteVariables);
  const updateVariable = useSetAtom(variableUpdateAtom({}));

  const toast = useToast();

  const addPendingChange = React.useCallback(
    (
      variableId: string,
      variableName: string,
      oldValue: number | null,
      newValue: number | null,
    ) => {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.set(variableId, { variableId, variableName, oldValue, newValue });
        return next;
      });
    },
    [setPendingChanges],
  );

  const clearPendingValue = React.useCallback(
    (variableId: string, variableName: string, oldValue: number | null) => {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.set(variableId, {
          variableId,
          variableName,
          oldValue,
          newValue: null,
        });
        return next;
      });
    },
    [setPendingChanges],
  );

  const revertChange = React.useCallback(
    (variableId: string) => {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(variableId);
        return next;
      });
    },
    [setPendingChanges],
  );

  const revertAll = React.useCallback(() => {
    setPendingChanges(new Map());
  }, [setPendingChanges]);

  const handleDateChange = React.useCallback(
    (date: CalendarDate) => {
      setEffectiveDate(date);
      setUserChangedDateTime(true);
    },
    [setEffectiveDate],
  );

  const handleTimeChange = React.useCallback(
    (time: Time | null) => {
      setEffectiveTime(time);
      setUserChangedDateTime(true);
    },
    [setEffectiveTime],
  );

  const activate = React.useCallback(async () => {
    let effectiveAt: string | undefined;

    if (userChangedDateTime) {
      const tz = getLocalTimeZone();
      const dateTime = new CalendarDateTime(
        effectiveDate.year,
        effectiveDate.month,
        effectiveDate.day,
        effectiveTime?.hour ?? 0,
        effectiveTime?.minute ?? 0,
        0,
      );
      effectiveAt = toZoned(dateTime, tz).toAbsoluteString();
    }

    const entries = Array.from(pendingChanges.values());
    const results = await Promise.allSettled(
      entries.map((change) =>
        updateVariable({
          id: change.variableId,
          value: change.newValue,
          effectiveAt,
        }),
      ),
    );

    const remaining = new Map<string, PendingVariableChange>();
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const change = entries[i];
      if (result.status === 'fulfilled') {
        const response = result.value?.pricingVariableUpdate;
        if (
          response?.validationErrors &&
          response.validationErrors.length > 0
        ) {
          toast.warning(
            `${change.variableName}: ${response.affectedTierCount ?? 0} affected tiers with validation warnings`,
          );
        }
      } else {
        remaining.set(change.variableId, change);
        const errorMessage =
          result.reason instanceof Error
            ? result.reason.message
            : 'Unknown error';
        toast.error(t.pricing.variables.toast.updateError(errorMessage));
      }
    }

    setPendingChanges(remaining);
    refetch?.();
    const tz = getLocalTimeZone();
    setEffectiveDate(today(tz));
    setEffectiveTime(null);
    setUserChangedDateTime(false);
  }, [
    userChangedDateTime,
    effectiveDate,
    effectiveTime,
    pendingChanges,
    updateVariable,
    setPendingChanges,
    refetch,
    setEffectiveDate,
    setEffectiveTime,
    toast,
    t,
  ]);

  return {
    pendingChanges,
    pendingCount,
    effectiveDate,
    effectiveTime,
    setEffectiveDate: handleDateChange,
    setEffectiveTime: handleTimeChange,
    addPendingChange,
    clearPendingValue,
    revertChange,
    revertAll,
    activate,
  };
};
